import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Search, UserPlus, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const EMPTY = {
  name: "", phone: "", email: "", password: "",
  gender: "", dob: "", blood_type: "", address: "",
  emergency_contact: "", allergies: "", conditions: "", notes: "",
};

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useSearchParams();
  const q = search.get("q") || "";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [creds, setCreds] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/patients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setPatients(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/patients/with-credentials", form);
      setCreds(data.credentials);
      toast.success("Patient registered with login");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`);
    toast.success("Credentials copied to clipboard");
  };

  return (
    <div className="space-y-5 fade-up" data-testid="patients-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Patients</h1>
          <p className="text-sm text-muted-foreground mt-1">{patients.length} patients in your practice</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              defaultValue={q}
              placeholder="Search by name / phone..."
              className="pl-9 w-64"
              onKeyDown={(e) => { if (e.key === "Enter") setSearch({ q: e.target.value }); }}
              data-testid="patients-search"
            />
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreds(null); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-patient-btn">
                <UserPlus className="size-4" /> New patient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              {creds ? (
                <>
                  <DialogHeader><DialogTitle>Patient account created</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 flex items-start gap-3">
                      <CheckCircle2 className="size-5 text-[hsl(var(--success))] mt-0.5" />
                      <div className="text-sm">
                        <div className="font-medium">Share these credentials with the patient.</div>
                        <div className="text-muted-foreground mt-0.5">They can sign in at {window.location.origin}/login.</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="p-3 bg-muted rounded-md flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Email</span>
                        <span className="mono font-medium">{creds.email}</span>
                      </div>
                      <div className="p-3 bg-muted rounded-md flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Password</span>
                        <span className="mono font-medium">{creds.password}</span>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={copyCreds} className="gap-2"><Copy className="size-4" /> Copy</Button>
                      <Button onClick={() => { setCreds(null); setOpen(false); }} data-testid="creds-done-btn">Done</Button>
                    </DialogFooter>
                  </div>
                </>
              ) : (
                <>
                  <DialogHeader><DialogTitle>Register new patient</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground -mt-2">Creates the patient record AND their portal login.</p>
                  <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2" data-testid="patient-form">
                    <div className="space-y-1.5"><Label>Full name *</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-field-name" /></div>
                    <div className="space-y-1.5"><Label>Email (for login) *</Label>
                      <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="patient-field-email" /></div>
                    <div className="space-y-1.5"><Label>Phone</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="patient-field-phone" /></div>
                    <div className="space-y-1.5"><Label>Initial password (auto if blank)</Label>
                      <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="auto-generated" data-testid="patient-field-password" /></div>
                    <div className="space-y-1.5"><Label>Gender</Label>
                      <Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="patient-field-gender" /></div>
                    <div className="space-y-1.5"><Label>Date of birth</Label>
                      <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="patient-field-dob" /></div>
                    <div className="space-y-1.5"><Label>Blood type</Label>
                      <Input value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} data-testid="patient-field-blood" /></div>
                    <div className="space-y-1.5"><Label>Emergency contact</Label>
                      <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
                    <div className="sm:col-span-2 space-y-1.5"><Label>Address</Label>
                      <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                    <div className="sm:col-span-2 space-y-1.5"><Label>Allergies</Label>
                      <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></div>
                    <div className="sm:col-span-2 space-y-1.5"><Label>Existing conditions</Label>
                      <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
                    <DialogFooter className="sm:col-span-2 mt-2">
                      <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                      <Button type="submit" data-testid="save-patient-btn">Create patient & login</Button>
                    </DialogFooter>
                  </form>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-3 px-5 font-semibold">Patient</th>
                <th className="py-3 px-5 font-semibold">Phone</th>
                <th className="py-3 px-5 font-semibold">DOB / Gender</th>
                <th className="py-3 px-5 font-semibold">Blood</th>
                <th className="py-3 px-5 font-semibold">Login</th>
                <th className="py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors">
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-3">
                      <Avatar className="size-9">
                        <AvatarFallback className="bg-accent/20 text-accent-foreground text-xs font-semibold">
                          {p.name?.slice(0,2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.email || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-5 mono text-xs">{p.phone || "—"}</td>
                  <td className="py-3 px-5 text-xs">{p.dob || "—"} • {p.gender || "—"}</td>
                  <td className="py-3 px-5 mono text-xs">{p.blood_type || "—"}</td>
                  <td className="py-3 px-5">{p.user_id ? <Badge variant="outline" className="text-[10px]">Portal</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="py-3 px-5 text-right">
                    <Link to={`/app/patients/${p.id}`}>
                      <Button variant="ghost" size="sm" data-testid={`view-patient-${p.id}`}>Open</Button>
                    </Link>
                  </td>
                </tr>
              ))}
              {patients.length === 0 && (
                <tr><td colSpan="6" className="py-12 text-center text-muted-foreground">No patients yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
